import { iConversation } from "@sdk/conversations/conversations.models";
import { iContact, iContactType, iGroupParticipant } from "@sdk/crm/crm.models";

const GROUP_FALLBACK_LABEL = "WhatsApp Group";
const PLACEHOLDER_NAMES = new Set(["n/a", "na", "unknown", "-"]);
const GROUP_CONTACT_TYPES: ReadonlySet<iContactType> = new Set([
  "WA_CLOUD_NATIVE_GROUP",
  "TELEGRAM_GROUP",
]);

type ConversationLike = Pick<iConversation, "data" | "contact"> & {
  connectionType?: string;
};
type ContactLike = Pick<
  iContact,
  "data" | "metaData" | "isGroupContact" | "contactType"
>;

const isUsableLabel = (value?: string | null) => {
  if (!value) {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  return !PLACEHOLDER_NAMES.has(trimmed.toLowerCase());
};

/**
 * True when the CRM contact represents a group chat (not a person).
 */
export const isGroupContact = (contact?: ContactLike | null): boolean => {
  if (!contact) {
    return false;
  }
  if (contact.isGroupContact === true) {
    return true;
  }
  if (contact.contactType && GROUP_CONTACT_TYPES.has(contact.contactType)) {
    return true;
  }
  return false;
};

/**
 * True when the conversation (or its nested/hydrated contact) is a group thread.
 */
export const isGroupConversation = (
  conversation?: ConversationLike | null,
  contact?: ContactLike | null,
): boolean => {
  if (isGroupContact(contact)) {
    return true;
  }
  if (
    conversation?.contact &&
    isGroupContact(conversation.contact as ContactLike)
  ) {
    return true;
  }
  const waGroupId = conversation?.data?.waGroupId as string | undefined;
  if (typeof waGroupId === "string" && waGroupId.trim()) {
    return true;
  }
  return false;
};

export const getGroupSuspended = (
  conversation?: ConversationLike | null,
  contact?: ContactLike | null,
): boolean => {
  if (conversation?.data?.waGroupSuspended === true) {
    return true;
  }
  if (contact?.data?.groupSuspended === true) {
    return true;
  }
  if (
    conversation?.contact &&
    (conversation.contact as ContactLike).data?.groupSuspended === true
  ) {
    return true;
  }
  return false;
};

/**
 * Omitted on 1-1 Cloud Native ⇒ treat as allowed.
 * Explicit `false` on group threads ⇒ blocked.
 */
export const getGroupSupportsInteractive = (
  conversation?: ConversationLike | null,
): boolean => {
  return conversation?.data?.supportsInteractive !== false;
};

/**
 * Omitted on 1-1 Cloud Native ⇒ treat as allowed.
 * Explicit `false` on group threads ⇒ blocked.
 */
export const getGroupSupportsCalling = (
  conversation?: ConversationLike | null,
): boolean => {
  return conversation?.data?.supportsCalling !== false;
};

const countActiveParticipants = (participants?: iGroupParticipant[] | null) => {
  if (!participants?.length) {
    return undefined;
  }
  return participants.filter((participant) => participant.status === "active")
    .length;
};

export const getActiveGroupParticipantCount = (
  conversation?: ConversationLike | null,
  contact?: ContactLike | null,
): number | undefined => {
  const fromConversation = conversation?.data?.waGroupParticipantCount;
  if (typeof fromConversation === "number" && fromConversation >= 0) {
    return fromConversation;
  }

  const fromContact = countActiveParticipants(
    contact?.metaData?.waGroupParticipants,
  );
  if (typeof fromContact === "number") {
    return fromContact;
  }

  if (conversation?.contact) {
    return countActiveParticipants(
      (conversation.contact as ContactLike).metaData?.waGroupParticipants,
    );
  }

  return undefined;
};

/**
 * Canonical agent-facing label for a WhatsApp / channel group contact.
 * Priority: conversation subject → contact.groupSubject → firstName → "WhatsApp Group".
 */
export const getGroupContactDisplayLabel = ({
  conversation,
  contact,
}: {
  conversation?: ConversationLike | null;
  contact?: ContactLike | null;
} = {}): string => {
  const fromConversation = conversation?.data?.waGroupSubject as
    | string
    | undefined;
  if (isUsableLabel(fromConversation)) {
    return fromConversation!.trim();
  }

  const groupSubject = contact?.data?.groupSubject;
  if (isUsableLabel(groupSubject)) {
    return groupSubject!.trim();
  }

  const nestedSubject = (conversation?.contact as ContactLike | undefined)?.data
    ?.groupSubject;
  if (isUsableLabel(nestedSubject)) {
    return nestedSubject!.trim();
  }

  const firstName =
    contact?.data?.firstName ||
    (conversation?.contact as ContactLike | undefined)?.data?.firstName;
  if (isUsableLabel(firstName)) {
    return firstName!.trim();
  }

  return GROUP_FALLBACK_LABEL;
};

export const getGroupContactSubtitle = ({
  conversation,
  contact,
}: {
  conversation?: ConversationLike | null;
  contact?: ContactLike | null;
} = {}): string => {
  if (getGroupSuspended(conversation, contact)) {
    return "Suspended · WhatsApp Group";
  }

  const count = getActiveGroupParticipantCount(conversation, contact);
  if (typeof count === "number") {
    const noun = count === 1 ? "participant" : "participants";
    return `${count} ${noun} · WhatsApp Group`;
  }

  return "WhatsApp Group";
};

/** Mongo filter: person contacts only (groups omitted / false). */
export const buildExcludeGroupContactsFilter = () => ({
  isGroupContact: { $ne: true },
});

/**
 * AND-merge exclusion of group contacts onto a contacts query.
 * Use for person pickers / invite targets — not for the global contacts page.
 */
export const applyExcludeGroupContactsFilter = (
  baseQuery: Record<string, unknown> = {},
): Record<string, unknown> => {
  const exclude = buildExcludeGroupContactsFilter();
  const keys = Object.keys(baseQuery);
  if (keys.length === 0) {
    return exclude;
  }

  if (Array.isArray(baseQuery.$and)) {
    return {
      ...baseQuery,
      $and: [...baseQuery.$and, exclude],
    };
  }

  return {
    $and: [baseQuery, exclude],
  };
};

/**
 * Drop group contact ids from a proactive 1-1 / invite-person recipient list.
 */
export const filterOutGroupContactIds = (
  contactIds: string[],
  resolveContact: (id: string) => ContactLike | null | undefined,
): string[] => {
  return contactIds.filter((id) => !isGroupContact(resolveContact(id)));
};

/**
 * Cloud Native connection allows Groups management when groupsEnabled is
 * omitted or true (backend default). Explicit false hides create/manage UI.
 */
export const isWACloudNativeGroupsEnabledOnConnection = (
  connection?: {
    type?: string;
    data?: { groupsEnabled?: boolean } | null;
  } | null,
): boolean => {
  if (!connection || connection.type !== "WHATS_APP_CLOUD_NATIVE") {
    return false;
  }
  return connection.data?.groupsEnabled !== false;
};
